import * as React from 'react';
import { Button } from '@teamsnap/teamsnap-ui';

import './MultiSelectPicker.scss';

type Props = {
  index?: number;
  placeholder?: string;
  options: { label: string; value: string }[];
  selected?: string[];
  updateSelectedOptions: (options: string[]) => void;
  containerMods?: string;
  disabled?: boolean;
  testId?: string;
};

const saveButtonId = 'multiselect-save-button';

const MultiSelectPicker = ({
  containerMods,
  index = 0,
  placeholder = 'Select',
  options,
  updateSelectedOptions,
  selected,
  disabled,
  testId = 'multiselect-picker',
}: Props) => {
  const [showOptions, toggleShowOptions] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (selected) {
      setSelectedOptions(selected);
    }
  }, [selected]);

  const closeAndSave = () => {
    toggleShowOptions(false);
    updateSelectedOptions(selectedOptions);
  };

  const handleBodyClick = React.useCallback((e) => {
    e.stopPropagation();
    const isTargetingPopup = e.target.closest('.multiselect-picker-container') != null;
    if (!isTargetingPopup) {
      document.getElementById(saveButtonId)?.click();
    }
  }, []);

  React.useEffect(() => {
    if (!showOptions) {
      window.removeEventListener('click', handleBodyClick, false);
    } else {
      window.addEventListener('click', handleBodyClick, false);
    }
  }, [showOptions, handleBodyClick]);

  const onKeyDown = () => {
    if (disabled) return;

    toggleShowOptions(!showOptions);
    if (showOptions) {
      closeAndSave();
    }
  };

  return (
    <div data-testid={ testId } className={ `multiselect-picker-container ${containerMods}` }>
      <div
        aria-disabled={ disabled }
        className="multiselect-picker-selected"
        onKeyDown={ onKeyDown }
        onClick={ () => !disabled && toggleShowOptions(true) }
        role="button"
        tabIndex={ 0 }
      >
        { selectedOptions.map((option) => (
          <div
            className={ `selected selected-${option.replace(/ /g, '')}-${index}` }
            key={ `selected-${option.replace(/ /g, '')}-${index}` }
          >
            <span title={ option }>{ options.find((x) => x.value === option)?.label }</span>
            <button
              type="button"
              onClick={ (e) => {
                e.stopPropagation();
                updateSelectedOptions(selectedOptions.filter((e) => e !== option));
              } }
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10.0002 11.229L5.97915 15.25C5.81248 15.4167 5.61115 15.4967 5.37515 15.49C5.13915 15.4827 4.93782 15.3957 4.77115 15.229C4.60448 15.0623 4.52115 14.8577 4.52115 14.615C4.52115 14.3717 4.60448 14.1667 4.77115 14L8.77115 10L4.75015 5.979C4.58348 5.81233 4.50348 5.60767 4.51015 5.365C4.51748 5.12167 4.60448 4.91667 4.77115 4.75C4.93782 4.58333 5.14248 4.5 5.38515 4.5C5.62848 4.5 5.83348 4.58333 6.00015 4.75L10.0002 8.771L14.0211 4.75C14.1878 4.58333 14.3925 4.5 14.6351 4.5C14.8785 4.5 15.0835 4.58333 15.2501 4.75C15.4168 4.91667 15.5001 5.12167 15.5001 5.365C15.5001 5.60767 15.4168 5.81233 15.2501 5.979L11.2292 10L15.2501 14.021C15.4168 14.1877 15.5001 14.389 15.5001 14.625C15.5001 14.861 15.4168 15.0623 15.2501 15.229C15.0835 15.3957 14.8785 15.479 14.6351 15.479C14.3925 15.479 14.1878 15.3957 14.0211 15.229L10.0002 11.229Z"
                  fill="#191919"
                />
              </svg>
            </button>
          </div>
        )) }
        { selectedOptions.length === 0 && <span className="placeholder">{ placeholder }</span> }
        <svg
          className="arrow"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.3 14.3L8.70001 11.7C8.38335 11.3833 8.31268 11.021 8.48801 10.613C8.66268 10.2043 8.97501 10 9.42501 10H14.575C15.025 10 15.3373 10.2043 15.512 10.613C15.6873 11.021 15.6167 11.3833 15.3 11.7L12.7 14.3C12.6 14.4 12.4917 14.475 12.375 14.525C12.2583 14.575 12.1333 14.6 12 14.6C11.8667 14.6 11.7417 14.575 11.625 14.525C11.5083 14.475 11.4 14.4 11.3 14.3Z"
            fill="#666666"
          />
        </svg>
      </div>
      { showOptions && (
        <div className="multiselect-picker-options" key={ `options-${index}` }>
          <div className="options">
            { options.map((option) => (
              <div className="Checkbox" key={ `option-${option.value.replace(/ /g, '')}-${index}` }>
                <input
                  className={ `multiselect-picker-${option.value.replace(
                    / /g,
                    ''
                  )} u-padRightXs Checkbox-input Checkbox--inline Checkbox--large` }
                  aria-checked="true"
                  tabIndex={ 0 }
                  type="checkbox"
                  id={ `multiselect-picker-${option.value.replace(/ /g, '')}` }
                  checked={ selectedOptions.includes(option.value) }
                  name={ `multiselect-picker-${option.value.replace(/ /g, '')}` }
                  value={ option.value }
                  onChange={ (e) => {
                    if (selectedOptions.includes(e.target.value)) {
                      setSelectedOptions(selectedOptions.filter((e) => e !== option.value));
                    } else {
                      setSelectedOptions([...selectedOptions, e.target.value]);
                    }
                  } }
                />
                <label className="Checkbox-label" htmlFor={ `multiselect-picker-${option.value.replace(/ /g, '')}` }>
                  { option.label }
                </label>
              </div>
            )) }
          </div>
          <div className="actions">
            <Button
              mods="btn-clear"
              onClick={ () => {
                setSelectedOptions([]);
              } }
            >
              Clear
            </Button>
            <Button otherProps={ { id: saveButtonId } } mods="btn-save" onClick={ closeAndSave }>
              Save
            </Button>
          </div>
        </div>
      ) }
    </div>
  );
};

export default MultiSelectPicker;
